import React from "react";
import ReactTooltip from "react-tooltip";

import TTSDebouncer from "../../foundation/TTSDebouncer";

import IconStyles, { IconStyle } from "../../types/enums/IconStyles";
import ButtonThemes, { ButtonTheme } from "../../types/enums/ButtonThemes";
import {
    Alignment,
    FlexBehaviour,
    Side,
    Size,
} from "../../types/system/Layout";
import { ButtonMenuContext } from "./ButtonMenu";
import Utilities from "../../foundation/Utility";

export interface ButtonProps {
    text?: string;
    bold?: boolean;
    icon?: string;
    iconStyle?: IconStyle;
    inline?: boolean;
    section?: boolean;
    dialer?: boolean;
    disabled?: boolean;
    theme?: ButtonTheme;
    fullWidth?: boolean;
    heightSize?: Size;
    transparent?: boolean;
    secure?: boolean;

    clearanceSide?: Side;
    clearanceSize?: Size;

    bufferSide?: Side;
    bufferSize?: Size;

    flexBehaviour?: FlexBehaviour;

    align?: Alignment;
    tooltip?: string;
    tooltipPlace?: string;
    className?: string;
    style?: React.CSSProperties;

    onClick?: () => void;
    onFocus?: () => void;
    onBlur?: () => void;
}

interface ButtonState {
    ref: React.RefObject<HTMLButtonElement>;
}

const tooltipDebouncer = new TTSDebouncer(100);

class Button extends React.Component<ButtonProps, ButtonState> {
    static contextType = ButtonMenuContext;
    context!: React.ContextType<typeof ButtonMenuContext>;

    state = {
        ref: React.createRef<HTMLButtonElement>(),
    };

    static getDerivedStateFromProps(
        props: ButtonProps,
        state: ButtonState,
    ): ButtonState {
        tooltipDebouncer.call(ReactTooltip.rebuild);

        return state;
    }

    focus() {
        this.state.ref.current?.focus();
    }

    onFocus() {
        this.props.onFocus?.();
    }

    onBlur() {
        this.props.onBlur?.();
    }

    onClick() {
        this.props.onClick?.();
        this.context.onClick();
    }

    render(): React.ReactNode {
        let classes = "normal-button";

        switch (this.props.theme) {
            case ButtonThemes.OK:
                classes = "ok-button";
                break;
            case ButtonThemes.Theme:
                classes = "theme-button";
                break;
            case ButtonThemes.Bad:
                classes = "bad-button";
                break;
            case ButtonThemes.Minimal:
                classes = "minimal-button";
                break;
            case ButtonThemes.None:
                classes = "none-button";
                break;
            case ButtonThemes.Auth:
                classes = "theme-button large";
                break;
            case ButtonThemes.Link:
                classes = "mock-link";
                break;
        }

        switch (this.props.align) {
            case Alignment.Left:
                classes += " left";
                break;
            case Alignment.Center:
                classes += " center";
                break;
            case Alignment.Right:
                classes += " right";
                break;
        }

        switch (this.props.heightSize) {
            case Size.Small:
                classes += " half-height";
                break;
            case Size.Large:
                classes += " double-height";
                break;
            case Size.XtraLarge:
                classes += " triple-height";
                break;
        }

        switch (this.props.flexBehaviour) {
            case "min":
                classes += " col-pane";
                break;
            case "max":
                classes += " col";
                break;
        }

        // Clearance classes
        if (!!this.props.clearanceSide) {
            classes += ` ${Utilities.getSideClass(
                this.props.clearanceSide,
            )}clearance${Utilities.getSizeClass(this.props.clearanceSize)}`;
        }

        // Buffer classes
        if (!!this.props.bufferSide) {
            classes += ` ${Utilities.getSideClass(
                this.props.bufferSide,
            )}buffer${Utilities.getSizeClass(this.props.bufferSize)}`;
        }

        if (this.props.fullWidth) classes += " full";

        if (this.props.bold) classes += " bold";

        if (this.props.inline) classes += " inline";

        if (this.props.section) classes += " section-button";

        if (this.props.dialer) classes += " panel dialer-button";

        if (this.props.transparent) classes += " transparent";

        if (this.props.className) classes += ` ${this.props.className}`;

        let iconStyle = this.props.iconStyle ?? IconStyles.Normal;

        return !this.props ? null : (
            <button
                ref={this.state.ref}
                tabIndex={this.props.disabled ? -1 : 0}
                className={`toolbar-button ${classes}`}
                style={this.props.style}
                disabled={this.props.disabled ?? false}
                data-tip={this.props.tooltip}
                data-place={this.props.tooltipPlace}
                onFocus={this.onFocus.bind(this)}
                onFocusCapture={this.context.onFocus}
                onBlur={this.onBlur.bind(this)}
                onBlurCapture={this.context.onBlur}
                onClick={this.onClick.bind(this)}>
                {this.props.secure && <i className={iconStyle}>lock</i>}
                {this.props.icon && (
                    <i className={iconStyle}>{this.props.icon}</i>
                )}
                {(this.props.text || !this.props.icon) && (
                    <span>{this.props.text ?? "Button"}</span>
                )}
            </button>
        );
    }
}

export default Button;
